import { swapImpact } from '../data';

// ── helpers ──

function fmtCash(n: number): string {
  const abs = Math.round(Math.abs(n));
  const sign = n < 0 ? '-' : n > 0 ? '+' : '';
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}$${abs.toLocaleString()}`;
  return `${sign}$${abs}`;
}

function fmtAbs(n: number): string {
  const abs = Math.round(Math.abs(n));
  if (abs >= 1000000) return `$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `$${abs.toLocaleString()}`;
  return `$${abs}`;
}

// ── per‑pair pipeline ──

interface PairRow {
  pair: string;
  swapLong: number;
  swapShort: number;
  holdDays: number;
  lotSize: number;
  tagline: string;
  // computed
  pipVal: number;
  longCost10d: number;
  shortCost10d: number;
  spreadCost: number;
  dominantCost: number;
  dominantDir: 'long' | 'short';
  dominantAnnual: number;
  swapPct: number;
  spreadPct: number;
}

const pipValues: Record<string, number> = {
  EURUSD: 10,
  GBPJPY: 7.5,
  USDTRY: 8,
};

const computedPairs: PairRow[] = swapImpact.map((item) => {
  const pipVal = pipValues[item.pair] ?? 10;
  const longCost10d = item.swapLong * pipVal * item.lotSize * item.holdDays;
  const shortCost10d = item.swapShort * pipVal * item.lotSize * item.holdDays;
  const spreadCost = 1.5 * pipVal * item.lotSize;

  const absLong = Math.abs(longCost10d);
  const absShort = Math.abs(shortCost10d);
  const dominantCost = Math.max(absLong, absShort);
  const dominantDir: 'long' | 'short' = absLong >= absShort ? 'long' : 'short';
  const dominantAnnual = (dominantDir === 'long' ? longCost10d : shortCost10d) * 25; // 250d ÷ 10

  const total = dominantCost + spreadCost;
  const swapPct = Math.round((dominantCost / total) * 100);
  const spreadPct = 100 - swapPct;

  return {
    ...item,
    pipVal,
    longCost10d,
    shortCost10d,
    spreadCost,
    dominantCost,
    dominantDir,
    dominantAnnual,
    swapPct,
    spreadPct,
  };
});

// ── global max for bar scaling ──

const maxDominant = Math.max(...computedPairs.map((p) => p.dominantCost));

// ── category styling ──

const pairMeta: Record<string, { label: string; accent: string; bg: string; border: string; text: string; bar: string }> = {
  EURUSD: {
    label: 'Major',
    accent: 'accent',
    bg: 'bg-accent-50/60',
    border: 'border-accent-200',
    text: 'text-accent-700',
    bar: 'bg-accent-500',
  },
  GBPJPY: {
    label: 'Cross',
    accent: 'secondary',
    bg: 'bg-secondary-50/60',
    border: 'border-secondary-200',
    text: 'text-secondary-700',
    bar: 'bg-secondary-500',
  },
  USDTRY: {
    label: 'Exotic',
    accent: 'primary',
    bg: 'bg-primary-50/60',
    border: 'border-primary-200',
    text: 'text-primary-700',
    bar: 'bg-primary-500',
  },
};

export default function SwapImpactSection() {
  const minCost = Math.min(...computedPairs.map((p) => p.dominantCost));
  const maxCost = maxDominant;
  const multiplier = maxCost / minCost;

  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* ═══════════ HEADER ═══════════ */}
        <div className="max-w-[680px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            What 10 nights of swap actually costs
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            One lot. Ten days. Three pairs. The numbers speak for themselves — and the gap is larger than most
            traders realize. All costs assume a 1.5-pip spread as baseline.
          </p>
        </div>

        {/* ═══════════ COST SCALE — proportional bars across the three pairs ═══════════ */}
        <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8 mb-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-foreground-400 mb-6">
            10‑day swap cost · worst direction · 1.0 lot
          </div>

          <div className="space-y-5">
            {computedPairs.map((p) => {
              const meta = pairMeta[p.pair] ?? pairMeta.EURUSD;
              const barW = Math.max((p.dominantCost / maxDominant) * 100, 3);
              const isExtreme = p.dominantCost >= 500;

              return (
                <div key={p.pair}>
                  {/* Pair label + cost */}
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="flex items-baseline gap-3">
                      <span className="text-[16px] font-[750] text-foreground-950 tracking-tight">
                        {p.pair}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${meta.bg} ${meta.text} border ${meta.border}`}>
                        {meta.label}
                      </span>
                      <span className="text-[12px] text-foreground-500">
                        {p.dominantDir === 'long' ? 'Long pays' : 'Short pays'}
                      </span>
                    </div>
                    <span className={`text-[18px] font-[800] tracking-tight tabular-nums ${isExtreme ? 'text-[#DC2626]' : 'text-foreground-800'}`}>
                      {fmtAbs(p.dominantCost)}
                    </span>
                  </div>

                  {/* Bar */}
                  <div className="w-full h-8 bg-background-50 rounded-md overflow-hidden relative">
                    <div
                      className={`h-full rounded-md flex items-center pl-3 transition-all duration-700 ${isExtreme ? 'bg-[#FCA5A5]/60' : meta.bar}`}
                      style={{ width: `${barW}%` }}
                    >
                      {barW >= 14 && (
                        <span className="text-[11px] font-bold text-background-50 whitespace-nowrap drop-shadow-sm">
                          {p.dominantDir === 'long'
                            ? `${p.swapLong.toFixed(2)} pips/day × 10 days`
                            : `${p.swapShort.toFixed(2)} pips/day × 10 days`}
                        </span>
                      )}
                    </div>
                    {barW < 14 && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-foreground-600 whitespace-nowrap">
                        {p.dominantDir === 'long'
                          ? `${p.swapLong.toFixed(2)} pips/day × 10 days`
                          : `${p.swapShort.toFixed(2)} pips/day × 10 days`}
                      </span>
                    )}
                  </div>

                  {/* Sub‑stats */}
                  <div className="flex items-center gap-5 mt-1.5 text-[11px] text-foreground-500">
                    <span>Daily swap rate: <strong className="text-foreground-700">{p.dominantDir === 'long' ? p.swapLong.toFixed(2) : p.swapShort.toFixed(2)} pips</strong></span>
                    <span>Annual projection: <strong className={`font-semibold ${p.dominantAnnual < -500 ? 'text-[#DC2626]' : 'text-foreground-700'}`}>{fmtCash(p.dominantAnnual)}</strong></span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Multiplier callout */}
          <div className="mt-7 pt-5 border-t border-background-100 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-foreground-950 flex items-center justify-center flex-shrink-0">
              <span className="text-[13px] font-[800] text-background-50">{Math.round(multiplier)}×</span>
            </div>
            <p className="text-[13px] leading-relaxed text-foreground-600">
              The <strong className="text-foreground-800">most expensive pair</strong> costs{' '}
              <strong className="text-foreground-800">{Math.round(multiplier)}×</strong> more than the cheapest —
              same lot size, same number of nights. The pair you choose is the single largest cost lever.
            </p>
          </div>
        </div>

        {/* ═══════════ DETAILED BREAKDOWN — three rich cards ═══════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 mb-8">
          {computedPairs.map((p) => {
            const meta = pairMeta[p.pair] ?? pairMeta.EURUSD;
            const isExtreme = p.dominantCost >= 500;
            const longIsNegative = p.longCost10d < 0;
            const shortIsNegative = p.shortCost10d < 0;
            const totalCost = p.dominantCost + p.spreadCost;

            return (
              <div
                key={p.pair}
                className={`relative bg-white rounded-xl border overflow-hidden transition-all duration-300 ${
                  isExtreme ? 'border-[#FCA5A5]/60 shadow-[0_0_0_1px_rgba(239,68,68,0.04)]' : 'border-background-200'
                }`}
              >
                {/* Left accent strip */}
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${meta.bar} rounded-l-xl`} />

                <div className="p-5 md:p-6 pl-6 md:pl-8">
                  {/* ── Header ── */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <span className="text-[20px] font-[750] text-foreground-950 tracking-tight">
                        {p.pair}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${meta.bg} ${meta.text} border ${meta.border}`}>
                        {meta.label}
                      </span>
                    </div>
                    {isExtreme && (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-red-50 text-[#DC2626] border border-red-200">
                        Extreme swap
                      </span>
                    )}
                  </div>

                  {/* ── Parameters strip ── */}
                  <div className="flex items-center gap-3 text-[11px] text-foreground-500 mb-5 pb-4 border-b border-background-50">
                    <span>1.0 lot</span>
                    <span className="text-foreground-300">·</span>
                    <span>10 days</span>
                    <span className="text-foreground-300">·</span>
                    <span>${p.pipVal}/pip</span>
                  </div>

                  {/* ── Direction breakdown ── */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {/* Long */}
                    <div className={`p-3 rounded-lg ${longIsNegative ? 'bg-[#FEF2F2]' : p.longCost10d > 0 ? 'bg-[#F0FDF4]' : 'bg-background-50'}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wide text-foreground-400 mb-2">
                        Long position
                      </div>
                      <div className="text-[12px] text-foreground-600 mb-1">
                        <span className="font-semibold text-foreground-800">{p.swapLong.toFixed(2)}</span> pips/day
                      </div>
                      <div className={`text-[18px] font-[800] tracking-tight tabular-nums ${
                        longIsNegative ? 'text-[#DC2626]' : p.longCost10d > 0 ? 'text-[#16A34A]' : 'text-foreground-400'
                      }`}>
                        {fmtCash(p.longCost10d)}
                      </div>
                      <div className="text-[10px] text-foreground-400 mt-0.5">
                        10‑day total
                      </div>
                    </div>

                    {/* Short */}
                    <div className={`p-3 rounded-lg ${shortIsNegative ? 'bg-[#FEF2F2]' : p.shortCost10d > 0 ? 'bg-[#F0FDF4]' : 'bg-background-50'}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wide text-foreground-400 mb-2">
                        Short position
                      </div>
                      <div className="text-[12px] text-foreground-600 mb-1">
                        <span className="font-semibold text-foreground-800">{p.swapShort.toFixed(2)}</span> pips/day
                      </div>
                      <div className={`text-[18px] font-[800] tracking-tight tabular-nums ${
                        shortIsNegative ? 'text-[#DC2626]' : p.shortCost10d > 0 ? 'text-[#16A34A]' : 'text-foreground-400'
                      }`}>
                        {fmtCash(p.shortCost10d)}
                      </div>
                      <div className="text-[10px] text-foreground-400 mt-0.5">
                        10‑day total
                      </div>
                    </div>
                  </div>

                  {/* ── Cost composition bar ── */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                        Cost composition
                      </span>
                      <span className="text-[11px] font-bold text-foreground-500">
                        Total {fmtAbs(totalCost)}
                      </span>
                    </div>

                    {/* Labels row */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold text-foreground-700">
                        Spread <span className="text-accent-500">{fmtAbs(p.spreadCost)}</span>
                      </span>
                      <span className="text-[11px] font-bold text-foreground-700">
                        Swap <span className={isExtreme ? 'text-[#DC2626]' : 'text-accent-500'}>{fmtAbs(p.dominantCost)}</span>
                      </span>
                    </div>

                    {/* Pure stacked bar */}
                    <div className="w-full h-2.5 bg-background-50 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-accent-300/60 transition-all duration-700"
                        style={{ width: `${p.spreadPct}%` }}
                      />
                      <div
                        className={`h-full transition-all duration-700 ${
                          isExtreme ? 'bg-[#FCA5A5]/70' : `${meta.bar}/70`
                        }`}
                        style={{ width: `${p.swapPct}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-sm bg-accent-300/60" />
                        <span className="text-[10px] text-foreground-500">Spread {p.spreadPct}%</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-sm ${isExtreme ? 'bg-[#FCA5A5]/70' : `${meta.bar}/70`}`} />
                        <span className="text-[10px] text-foreground-500">Swap {p.swapPct}%</span>
                      </div>
                    </div>
                  </div>

                  {/* ── Annual projection ── */}
                  <div className={`p-3 rounded-lg ${isExtreme ? 'bg-[#FEF2F2]' : meta.bg} mb-4`}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-foreground-400 mb-1">
                      Annual projection · {p.dominantDir} position
                    </div>
                    <div className={`text-[20px] font-[800] tracking-tight tabular-nums ${isExtreme ? 'text-[#DC2626]' : 'text-foreground-800'}`}>
                      {fmtCash(p.dominantAnnual)}
                    </div>
                    <div className="text-[10px] text-foreground-500 mt-0.5">
                      25 cycles × 10 days each · constant lot size
                    </div>
                  </div>

                  {/* ── Tagline ── */}
                  <p className={`text-[12px] leading-relaxed font-semibold ${meta.text}`}>
                    {p.tagline}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* ═══════════ BOTTOM CROSS‑COMPARISON ═══════════ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-3">
              Pair selection leverage
            </div>
            <p className="text-[13px] leading-relaxed text-foreground-700">
              At 10 days and 1 lot, the swap cost gap between{' '}
              <strong className="text-foreground-900">EURUSD</strong> and{' '}
              <strong className="text-foreground-900">USDTRY</strong> is{' '}
              <span className="text-[#DC2626] font-[800] text-[20px]">{Math.round(multiplier)}×</span>.
              That means every dollar of swap you budget for EURUSD becomes{' '}
              <strong className="text-foreground-900">{Math.round(multiplier)} dollars</strong> on USDTRY.
              No amount of trade optimization closes a gap that wide — you choose the pair, or the pair chooses
              your cost structure.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-3">
              Direction is not optional
            </div>
            <p className="text-[13px] leading-relaxed text-foreground-700">
              On <strong className="text-foreground-900">USDTRY</strong>, going long costs{' '}
              <strong className="text-[#DC2626]">{fmtAbs(computedPairs[2].longCost10d)}</strong> over 10 days. Going
              short <strong className="text-[#16A34A]">earns</strong> you{' '}
              <strong className="text-[#16A34A]">{fmtAbs(computedPairs[2].shortCost10d)}</strong>.
              That is a{' '}
              <span className="font-[800] text-foreground-900">
                {Math.round(Math.abs(computedPairs[2].longCost10d) / Math.abs(computedPairs[2].shortCost10d))}×
              </span>{' '}
              swing based solely on which button you click. On pairs with extreme rate differentials, direction
              isn't a preference — it is the entire trade.
            </p>
          </div>
        </div>

        {/* ═══════════ DISCLAIMER ═══════════ */}
        <p className="mt-6 text-[12px] text-foreground-500 text-center max-w-[640px] mx-auto">
          Swap rates are indicative snapshots. Actual rates vary by broker, update daily at 5 PM ET, and include
          broker-specific markups. Pip values: EURUSD $10, GBPJPY ¥1,000 (~$7.50), USDTRY ~$8.00. Spread assumed
          at 1.5 pips. Annual projection multiplies 10‑day cost by 25 cycles — real trading frequency varies.
        </p>
      </div>
    </section>
  );
}